import { logActivity } from './activityLogApi';

const getHeaders = (organizationId) => {
  const region = sessionStorage.getItem('userRegion') || 'AP';
  return {
    'Content-Type': 'application/json',
    'X-Tenant-Id': organizationId,
    'X-Country-Code': region,
  };
};

const API_URLS = {
  initiateScan:   'https://5n56s0uwa8.execute-api.ap-southeast-2.amazonaws.com/prod',
  listScans:      'https://wcz6ej0g0a.execute-api.ap-southeast-2.amazonaws.com/prod',
  scanStatus:     'https://qyjw19sv68.execute-api.ap-southeast-2.amazonaws.com/prod',
  report:         'https://4o7g5ofe50.execute-api.ap-southeast-2.amazonaws.com/prod',
  analyze:        'https://8cgbzk4id8.execute-api.ap-southeast-2.amazonaws.com/prod',
  analyzeStatus:  'https://nwxxj4bfwf.execute-api.ap-southeast-2.amazonaws.com/prod',
  reportGenerate: 'https://tp82kg514d.execute-api.ap-southeast-2.amazonaws.com/prod',
  reportStatus:   'https://gs8z7o18sc.execute-api.ap-southeast-2.amazonaws.com/prod',
  stopScan:    'https://xcfdmmtvyh.execute-api.ap-southeast-2.amazonaws.com/prod',
  deleteScan:  'https://6bacdvktvj.execute-api.ap-southeast-2.amazonaws.com/prod',
};

export const scanApi = {

  // POST — Queue a new scan
  initiateScan: async (organizationId, targetUrl, scanType = 'FULL', userData = null) => {
    const res = await fetch(`${API_URLS.initiateScan}/api/v1/scans`, {
      method: 'POST',
      headers: getHeaders(organizationId),
      body: JSON.stringify({ targetUrl, scanType }),
    });

    if (!res.ok) throw new Error(`Scan initiation failed: ${res.status}`);

    const result = await res.json();

    // Log AFTER the API call succeeds, using the real scanId from the response
    logActivity({
      userData,
      orgId:       organizationId,
      region:      sessionStorage.getItem('userRegion') || 'AP',
      module:      'DAST',
      category:    'Risk',
      action:      'Scan Initiated',
      severity:    'warning',
      description: `DAST scan started for ${targetUrl} (${scanType})`,
      metadata:    { targetUrl, scanType, scanId: result.scanId },
    }).catch(console.error); // silent fail — never block the scan

    return result;
  },

  // GET — List all scans (read-only, no logging needed)
  listScans: async (organizationId) => {
    const res = await fetch(`${API_URLS.listScans}/api/v1/scans`, {
      headers: getHeaders(organizationId),
    });
    if (!res.ok) throw new Error(`Failed to list scans: ${res.status}`);
    return res.json();
  },

  // GET — Scan status (polling, no logging needed)
  getScanStatus: async (organizationId, scanId) => {
    const res = await fetch(`${API_URLS.scanStatus}/api/v1/scans/${scanId}/status`, {
      headers: getHeaders(organizationId),
    });
    if (!res.ok) throw new Error(`Failed to get status: ${res.status}`);
    return res.json();
  },

  // GET — Download raw report PDF (no logging needed — just a download)
  getReport: async (organizationId, scanId) => {
    const res = await fetch(`${API_URLS.report}/api/v1/scans/${scanId}/report`, {
      headers: getHeaders(organizationId),
    });
    if (!res.ok) throw new Error(`Failed to get report: ${res.status}`);
    const contentType = res.headers.get('Content-Type');
    if (contentType && contentType.includes('application/pdf')) {
      return res.blob();
    }
    return res.json();
  },

  // POST — Start AI analysis
  startAnalysis: async (organizationId, scanId, userData = null) => {
    const res = await fetch(`${API_URLS.analyze}/api/v1/scans/${scanId}/analyze`, {
      method: 'POST',
      headers: getHeaders(organizationId),
    });

    if (!res.ok) throw new Error(`Analysis failed: ${res.status}`);

    const result = await res.json();

    logActivity({
      userData,
      orgId:       organizationId,
      region:      sessionStorage.getItem('userRegion') || 'AP',
      module:      'DAST',
      category:    'Risk',
      action:      'AI Analysis Started',
      severity:    'info',
      description: `AI analysis started for scan ${scanId}`,
      metadata:    { scanId, jobId: result.jobId },
    }).catch(console.error);

    return result;
  },

  // GET — Poll analysis status (polling, no logging needed)
  getAnalysisStatus: async (organizationId, jobId) => {
    const res = await fetch(`${API_URLS.analyzeStatus}/api/v1/scans/analyze/status/${jobId}`, {
      headers: getHeaders(organizationId),
    });
    if (!res.ok) throw new Error(`Failed to get analysis status: ${res.status}`);
    return res.json();
  },

  // Convenience: Start + Poll until done
  analyzeAndWait: async (organizationId, scanId, { onProgress, pollInterval = 5000, timeout = 300000, userData = null } = {}) => {
    // startAnalysis already logs "AI Analysis Started"
    const { jobId } = await scanApi.startAnalysis(organizationId, scanId, userData);

    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = setInterval(async () => {
        try {
          if (Date.now() - startTime > timeout) {
            clearInterval(poll);
            reject(new Error('Analysis timed out'));
            return;
          }

          const result = await scanApi.getAnalysisStatus(organizationId, jobId);
          if (onProgress) onProgress(result);

          if (result.status === 'COMPLETED') {
            clearInterval(poll);

            // Log completion when polling confirms COMPLETED
            logActivity({
              userData,
              orgId:       organizationId,
              region:      sessionStorage.getItem('userRegion') || 'AP',
              module:      'DAST',
              category:    'Risk',
              action:      'AI Analysis Completed',
              severity:    'success',
              description: `AI analysis completed for scan ${scanId}`,
              metadata:    { scanId, jobId },
            }).catch(console.error);

            resolve(result);

          } else if (result.status === 'FAILED') {
            clearInterval(poll);

            logActivity({
              userData,
              orgId:       organizationId,
              region:      sessionStorage.getItem('userRegion') || 'AP',
              module:      'DAST',
              category:    'Risk',
              action:      'AI Analysis Failed',
              severity:    'critical',
              description: `AI analysis failed for scan ${scanId}`,
              metadata:    { scanId, jobId, error: result.error },
            }).catch(console.error);

            reject(new Error(result.error || 'Analysis failed'));
          }
        } catch (err) {
          clearInterval(poll);
          reject(err);
        }
      }, pollInterval);
    });
  },

  // POST — Trigger report generation
  generateReport: async (organizationId, scanId, userData = null) => {
    const res = await fetch(`${API_URLS.reportGenerate}/api/v1/scans/${scanId}/report/generate`, {
      method: 'POST',
      headers: getHeaders(organizationId),
    });

    if (!res.ok) throw new Error(`Report generation failed: ${res.status}`);

    const result = await res.json();

    logActivity({
      userData,
      orgId:       organizationId,
      region:      sessionStorage.getItem('userRegion') || 'AP',
      module:      'DAST',
      category:    'Data',
      action:      'Report Generation Started',
      severity:    'info',
      description: `Report generation triggered for scan ${scanId}`,
      metadata:    { scanId },
    }).catch(console.error);

    return result;
  },

  // GET — Poll report status (polling, no logging needed)
  getReportStatus: async (organizationId, scanId) => {
    const res = await fetch(`${API_URLS.reportStatus}/api/v1/scans/${scanId}/report/status`, {
      headers: getHeaders(organizationId),
    });
    if (!res.ok) throw new Error(`Failed to get report status: ${res.status}`);
    return res.json();
  },

  // POST — Initiate domain verification
  initiateVerification: async (organizationId, targetUrl, userData = null) => {
    const region = sessionStorage.getItem('userRegion') || 'AP';
    const res = await fetch(
      `https://x9ea3e2rpe.execute-api.ap-southeast-2.amazonaws.com/prod/api/v1/organizations/${organizationId}/domains/verify/initiate`,
      {
        method: 'POST',
        headers: getHeaders(organizationId),
        body: JSON.stringify({ targetUrl, region }),
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Verification initiation failed: ${res.status}`);
    }

    const result = await res.json();

    logActivity({
      userData,
      orgId:       organizationId,
      region,
      module:      'DAST',
      category:    'Settings',
      action:      'Domain Verification Initiated',
      severity:    'info',
      description: `Domain verification started for ${targetUrl}`,
      metadata:    { targetUrl, verificationToken: result.verificationToken },
    }).catch(console.error);

    return result;
  },

  // POST — Check if DNS TXT record is in place
  verifyDomain: async (organizationId, domain, userData = null) => {
    const region = sessionStorage.getItem('userRegion') || 'AP';
    const res = await fetch(
      `https://vz22wzx0b6.execute-api.ap-southeast-2.amazonaws.com/prod/api/v1/organizations/${organizationId}/domains/verify/check`,
      {
        method: 'POST',
        headers: getHeaders(organizationId),
        body: JSON.stringify({ domain, region }),
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Verification check failed: ${res.status}`);
    }

    const result = await res.json();

    // Only log if verification actually passed
    if (result.verified) {
      logActivity({
        userData,
        orgId:       organizationId,
        region,
        module:      'DAST',
        category:    'Settings',
        action:      'Domain Verified',
        severity:    'success',
        description: `Domain successfully verified for scanning: ${domain}`,
        metadata:    { domain },
      }).catch(console.error);
    }

    return result;
  },

  // GET — Get verification status (read-only, no logging needed)
  getVerificationStatus: async (organizationId, domain) => {
    const region = sessionStorage.getItem('userRegion') || 'AP';
    const res = await fetch(
      `https://y0c19uup9b.execute-api.ap-southeast-2.amazonaws.com/prod/api/v1/organizations/${organizationId}/domains/verify/status?domain=${encodeURIComponent(domain)}&region=${region}`,
      { headers: getHeaders(organizationId) }
    );
    if (!res.ok) throw new Error(`Failed to get verification status: ${res.status}`);
    return res.json();
  },

  // GET — List all verified domains (read-only, no logging needed)
  listVerifiedDomains: async (organizationId) => {
    const region = sessionStorage.getItem('userRegion') || 'AP';
    const res = await fetch(
      `https://5jrargsmge.execute-api.ap-southeast-2.amazonaws.com/prod/api/v1/organizations/${organizationId}/domains/verified?region=${region}`,
      { headers: getHeaders(organizationId) }
    );
    if (!res.ok) throw new Error(`Failed to list verified domains: ${res.status}`);
    return res.json();
  },


  // POST — Stop a QUEUED or RUNNING scan
stopScan: async (organizationId, scanId, userData = null) => {
  const res = await fetch(`${API_URLS.stopScan}/api/v1/scans/${scanId}/stop`, {
    method: 'POST',
    headers: getHeaders(organizationId),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Stop scan failed: ${res.status}`);
  }

  const result = await res.json();

  logActivity({
    userData,
    orgId:       organizationId,
    region:      sessionStorage.getItem('userRegion') || 'AP',
    module:      'DAST',
    category:    'Risk',
    action:      'Scan Stopped',
    severity:    'warning',
    description: `DAST scan stopped: ${scanId}`,
    metadata:    { scanId },
  }).catch(console.error);

  return result;
},

// DELETE — Remove a scan record permanently
deleteScan: async (organizationId, scanId, userData = null) => {
  const res = await fetch(`${API_URLS.deleteScan}/api/v1/scans/${scanId}`, {
    method: 'DELETE',
    headers: getHeaders(organizationId),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Delete scan failed: ${res.status}`);
  }

  logActivity({
    userData,
    orgId:       organizationId,
    region:      sessionStorage.getItem('userRegion') || 'AP',
    module:      'DAST',
    category:    'Risk',
    action:      'Scan Deleted',
    severity:    'info',
    description: `Scan record deleted: ${scanId}`,
    metadata:    { scanId },
  }).catch(console.error);

  return res.json();
},
};